// src/careReferral/pages/requestOrReferralPage.tsx
// -----------------------------------------------------------------------------
// Section-level validation badges on collapsed headers:
//  - "Required" shows "<n> missing" until Name/Email/Phone are valid.
//  - "Location" (Broadcast) shows "ZIP required" or "City required".
//  - Badges flip to "Complete" (success) once satisfied.
// Compact Optional + Amenities with live summaries.
// Auto-generated non-required Reference (REF-XXXX-XXXX), with "Change" + validation.
// Minimal required fields: Request Type (radio), Your Name, Email, Phone.
// Max Providers is controlled at the top next to the Broadcast toggle.
// Friendly error handling: client ZIP validation, Apollo errorPolicy: "all".
// Country is fixed to United States and hidden.
// Move-in timing UX: ASAP / 1 week / 30 days / Pick a date.
// Broadcast ZIP radius clamped to 1..20.
// -----------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Divider, MenuItem, Snackbar, Stack,
  TextField, Typography, Alert as MUIAlert, FormControlLabel, Switch, Dialog, DialogTitle,
  DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails, RadioGroup, Radio,
  FormControl, FormHelperText, Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { useSubmitInquiry, RequestKind } from "../../../../platform/careComponents/hooks/useSubmitInquiry";
import { useDuplicateDialog } from "../../../../platform/careComponents/hooks/useDuplicateDialog";
import { DuplicateDialog } from "../../../../platform/careComponents/components/duplicateDialog";
import CareResidentDetailsFields, {
  type CareResidentDetailsFieldValue,
} from "../../../care/components/CareResidentDetailsFields";
import { GET_BUSINESS_BY_URL } from "../../../../platform/graphql/queries";
import { CREATE_REFERRAL_LEAD_BROADCAST } from "../../../../platform/graphql/mutations/care";
import { SEND_EMAIL_MUTATION } from "../../../../platform/graphql/mutations/business";
import {
  AMENITY_LABELS,
  AmenityFlags,
  CARE_NEED_OPTIONS,
  FALLBACK_SOURCE_TYPES,
  KEY_RE,
  PAYER_OPTIONS,
  PayerType,
  PricingMode,
  UrgencyChoice,
  buildAmenitiesInput,
  computeUrgency,
  generatePatientKey,
  getFirstApolloMessage,
  isAuthRequiredMessage,
  isNoMatchesMessage,
  isValidEmail,
  isValidPhone,
  isZipValid,
  normalizeResidentKey,
  stableStringify,
  summarizeList,
  toNumId,
} from "./requestOrReferralShared";

/* ----------------- GraphQL ----------------- */
const GET_REFERRAL_SOURCE_TYPES = gql`
  query ReferralSourceTypes { referralSourceTypes { id code name } }
`;
const GET_MY_REFERRER_PROFILE = gql`
  query GetMyReferrerProfile { myReferrerProfile { id contactName orgName email phone } }
`;
const GET_ALL_COUNTRIES = gql`
  query GetAllCountries {
    getAllCountries {
      id name
      states { id name cities { id name } }
    }
  }
`;

function useQueryParam(name: string) {
  const { search } = useLocation();
  return React.useMemo(() => {
    const raw = new URLSearchParams(search).get(name);
    if (raw == null) return null;
    const v = raw.trim().toLowerCase();
    return v === "" || v === "undefined" || v === "null" ? null : v;
  }, [name, search]);
}

/* ============================================================================ */
export default function RequestOrReferralPage() {
  const location = useLocation();
  const authReturnTo = encodeURIComponent(`${location.pathname}${location.search}${location.hash}`);
  const loginHref = `/login?returnTo=${authReturnTo}`;
  const signupHref = `/signup?returnTo=${authReturnTo}`;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const attributionAgentId = useMemo(
    () => String(searchParams.get("agentId") || "").trim() || null,
    [searchParams]
  );
  const attributionAgentName = useMemo(
    () => String(searchParams.get("agentName") || "").trim() || null,
    [searchParams]
  );
  const attributionSourceType = useMemo(() => {
    const raw = String(searchParams.get("leadSourceType") || searchParams.get("sourceType") || "").trim().toUpperCase();
    if (raw === "AGENT_SOURCED" || raw === "PLATFORM_DIRECT") return raw;
    if (attributionAgentId || attributionAgentName) return "AGENT_SOURCED";
    return "PLATFORM_DIRECT";
  }, [searchParams, attributionAgentId, attributionAgentName]);

  /* Route & business scope */
  const { businessUrl } = useParams<{ businessUrl: string }>();
  const qpMode = useQueryParam("mode");
  const qpSend = useQueryParam("send");
  const modeParam = qpMode ?? qpSend;
  const bizByUrlQ = useQuery(GET_BUSINESS_BY_URL, {
    variables: { businessUrl: businessUrl as string },
    skip: !businessUrl,
    fetchPolicy: "cache-and-network",
  });
  const slugBizId = toNumId(bizByUrlQ.data?.businessByUrl?.id);
  const forceBroadcast = modeParam === "broadcast";

  /* User / profile */
  const userContext = React.useContext(UserContext);
  const user = userContext.providerUser;
  const { data: myReferrerData, loading: myReferrerLoading, error: myReferrerError } =
    useQuery(GET_MY_REFERRER_PROFILE, { fetchPolicy: "cache-first" });
  const myReferrerId: number | null =
    (myReferrerData?.myReferrerProfile?.id != null ? Number(myReferrerData.myReferrerProfile.id) : null) ?? null;

  /* Form state */
  const [pricingRush, setPricingRush] = useState(false);
  const [kind, setKind] = useState<RequestKind>("availability");
  const [companyName, setCompanyName] = useState(""); // optional
  const [referrerName, setReferrerName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [patientKey, setPatientKey] = useState<string>(""); // auto-generated
  const [careNeed, setCareNeed] = useState("");
  const [payerType, setPayerType] = useState<PayerType>("");
  const pricingMode: PricingMode | null = React.useMemo(
    () => (payerType === "PRIVATE" ? "CONCIERGE" : payerType ? "SELF_SERVE" : null),
    [payerType]
  );
  const [medicaidConfirmed, setMedicaidConfirmed] = useState(false);
  const [notes, setNotes] = useState("");
  const [residentAge, setResidentAge] = useState("");
  const [residentGender, setResidentGender] = useState("");
  const [residentCurrentSituation, setResidentCurrentSituation] = useState("");
  const [residentCurrentLocation, setResidentCurrentLocation] = useState("");
  const [residentBudget, setResidentBudget] = useState("");
  const [residentPrivateRoomNeeded, setResidentPrivateRoomNeeded] = useState("");
  const [residentCaregiverPreference, setResidentCaregiverPreference] = useState("");

  // Move-in timing
  const [urgencyChoice, setUrgencyChoice] = useState<UrgencyChoice>("UNKNOWN");
  const [targetDate, setTargetDate] = useState<string>(""); // only when "DATE"

  const [consent, setConsent] = useState(false); // optional
  const [referralSourceTypeId, setReferralSourceTypeId] = useState<number | "">("");

  /* Broadcast filters */
  const [maxInvites, setMaxInvites] = useState<number>(5);
  const DEFAULT_COUNTRY_ID = 228; // US (fixed/hidden)
  const [searchCountryId, setSearchCountryId] = useState<number | null>(DEFAULT_COUNTRY_ID);
  const [searchStateId, setSearchStateId] = useState<number | null>(null);
  const [searchCityId, setSearchCityId] = useState<number | null>(null);
  const [searchZip, setSearchZip] = useState<string>("");
  const [searchRadiusMiles, setSearchRadiusMiles] = useState<number | null>(15);
  type LocationMode = "ZIP" | "CITY";
  const [locationMode, setLocationMode] = useState<LocationMode>("ZIP");

  const [mustBeClaimed, setMustBeClaimed] = useState(true);
  // amenities flags
  const [amenities, setAmenities] = useState<AmenityFlags>({
    isPrivateRoom: false,
    hasPrivateBathroom: false,
    hasHospitalBed: false,
    hasHoyerLift: false,
    hasOxygen: false,
    accommodatesBariatric: false,
  });

  /* Reference editor dialog state */
  const [keyEditorOpen, setKeyEditorOpen] = useState(false);
  const [keyDraft, setKeyDraft] = useState<string>("");

  /* Error UX state */
  const [friendlyError, setFriendlyError] = useState<string | null>(null);
  const [confirmationEmailWarning, setConfirmationEmailWarning] = useState<string | null>(null);
  const [noMatches, setNoMatches] = useState(false);

  /* switches */
  const initialSendMethod = React.useMemo<"TARGETED" | "BROADCAST">(
    () => (forceBroadcast ? "BROADCAST" : slugBizId ? "TARGETED" : "BROADCAST"),
    [forceBroadcast, slugBizId]
  );
  const [sendMethod, setSendMethod] = useState<"TARGETED" | "BROADCAST">(initialSendMethod);
  const [broadcastTrackUrl, setBroadcastTrackUrl] = useState<string | null>(null);
  const [broadcastInviteCount, setBroadcastInviteCount] = useState<number | null>(null);

  /* lookups (only need states/cities; country UI hidden but data still used) */
  const { data: srcTypesData, loading: srcTypesLoading, error: srcTypesError } =
    useQuery(GET_REFERRAL_SOURCE_TYPES, { fetchPolicy: "cache-and-network" });
  const srcTypesRaw = srcTypesData?.referralSourceTypes;
  const srcTypesList: { id: number; code: string; name: string }[] =
    Array.isArray(srcTypesRaw) && srcTypesRaw.length > 0 ? srcTypesRaw : FALLBACK_SOURCE_TYPES;

  const { data: countryDataRaw, loading: loadingCountries } =
    useQuery(GET_ALL_COUNTRIES, { fetchPolicy: "cache-and-network" });

  type City = { id: number | string; name: string };
  type StateType = { id: number | string; name: string; cities?: City[] };
  type CountryType = { id: number | string; name: string; states?: StateType[] };
  const countries: CountryType[] = countryDataRaw?.getAllCountries ?? [];
  const eqId = (a: unknown, b: unknown) => Number(a) === Number(b);
  const asSelectValue = (id?: number | null) => (id != null ? String(id) : "");
  const selectedCountry = countries.find((c) => eqId(c.id, searchCountryId)) ?? null;
  const selectedState = selectedCountry?.states?.find((s) => eqId(s.id, searchStateId)) ?? null;
  const hasStates = Boolean(selectedCountry?.states?.length);
  const hasCities = Boolean(selectedState?.cities?.length);

  /* UX & guards */
  useEffect(() => {
    if (pricingMode !== "CONCIERGE" && pricingRush) {
      setPricingRush(false);
    }
  }, [pricingMode, pricingRush]);

  // If Concierge + Rush, pre-select ASAP (do not force it back if user changes later)
  useEffect(() => {
    if (pricingRush && urgencyChoice !== "ASAP") {
      setUrgencyChoice("ASAP");
    }
  }, [pricingRush, urgencyChoice]);

  const navigate = useNavigate();
  const isProviderScoped = Boolean(slugBizId);

  useEffect(() => {
    if (!isProviderScoped && sendMethod !== "BROADCAST") setSendMethod("BROADCAST");
  }, [isProviderScoped, sendMethod]);

  const goPickProvider = () => navigate("/care");

  useEffect(() => {
    if (searchCountryId == null) setSearchCountryId(DEFAULT_COUNTRY_ID);
  }, [searchCountryId]);

  /* Prefill once */
  const didPrefillRef = useRef(false);
  useEffect(() => {
    if (didPrefillRef.current) return;
    setPatientKey(generatePatientKey()); // generate on load

    const r = myReferrerData?.myReferrerProfile;
    const preferredCompany = r?.orgName || (user as any)?.orgName || "";
    const preferredName = r?.contactName || "";
    const preferredEmail = r?.email || (user as any)?.email || "";
    const preferredPhone = r?.phone || (user as any)?.phone || "";

    if (!companyName && preferredCompany) setCompanyName(preferredCompany);
    if (!referrerName && preferredName) setReferrerName(preferredName);
    if (!email && preferredEmail) setEmail(preferredEmail);
    if (!phone && preferredPhone) setPhone(preferredPhone);

    didPrefillRef.current = true;
  }, [myReferrerData, user, companyName, referrerName, email, phone]);

  /* On-behalf check */
  const emailLower = (email || "").trim().toLowerCase();
  const primaryAccountEmail = useMemo(() => {
    const candidates = [
      (user as any)?.email,
      (user as any)?.user?.email,
      myReferrerData?.myReferrerProfile?.email,
    ].filter(Boolean) as string[];
    return candidates.length ? candidates[0].trim().toLowerCase() : "";
  }, [user, myReferrerData]);

  const isLoggedInLike = Boolean(user) || Boolean(myReferrerId);
  const [confirmOnBehalfOpen, setConfirmOnBehalfOpen] = useState(false);
  const [submittingOnBehalf, setSubmittingOnBehalf] = useState(false);
  const needsOnBehalfConfirm =
    isLoggedInLike && !!primaryAccountEmail && !!emailLower && primaryAccountEmail !== emailLower && !submittingOnBehalf;

  useEffect(() => {
    if (primaryAccountEmail && emailLower === primaryAccountEmail && submittingOnBehalf) {
      setSubmittingOnBehalf(false);
    }
  }, [emailLower, primaryAccountEmail, submittingOnBehalf]);

  /* Derived validity - minimal required */
  const emailValid = isValidEmail(email);
  const phoneValid = isValidPhone(phone);
  const baseRequiredValid = !!referrerName.trim() && emailValid && phoneValid && !!kind;
  const needsPayerType = !payerType;
  const isPublicProgramPayer = payerType === "MEDICAID" || payerType === "MEDICARE" || payerType === "VA";

  // location rules (Broadcast)
  const needsZipInZipMode = sendMethod === "BROADCAST" && locationMode === "ZIP" && !searchZip.trim();
  const needsCityInCityMode = sendMethod === "BROADCAST" && locationMode === "CITY" && !searchCityId;

  // invalid ZIP format guard (only if user typed something)
  const invalidZip =
    sendMethod === "BROADCAST" &&
    locationMode === "ZIP" &&
    !!searchZip.trim() &&
    !isZipValid(searchZip);

  // payer is OPTIONAL; Medicaid confirm only if chosen
  const needsMedicaidConfirm = payerType === "MEDICAID" && !medicaidConfirmed;

  const canSubmit =
    baseRequiredValid &&
    !needsPayerType &&
    !needsZipInZipMode &&
    !needsCityInCityMode &&
    !invalidZip &&
    !needsMedicaidConfirm;

  /* --- Section-level issues for header badges --- */
  const requiredIssues: string[] = useMemo(() => {
    const arr: string[] = [];
    if (needsPayerType) arr.push("Payer");
    if (!referrerName.trim()) arr.push("Name");
    if (!emailValid) arr.push("Email");
    if (!phoneValid) arr.push("Phone");
    return arr;
  }, [needsPayerType, referrerName, emailValid, phoneValid]);

  const locationIssues: string[] = useMemo(() => {
    if (sendMethod !== "BROADCAST") return [];
    if (invalidZip) return ["ZIP invalid"];
    if (needsZipInZipMode) return ["ZIP required"];
    if (needsCityInCityMode) return ["City required"];
    return [];
  }, [sendMethod, invalidZip, needsZipInZipMode, needsCityInCityMode]);

  /* Submit infra */
  const duplicateDialog = useDuplicateDialog();
  const { submit, submitting, error: submitError, toast, clearToast, trackUrl } =
    useSubmitInquiry({ onDuplicateLegacy: duplicateDialog.onDuplicate as any, passResidentKeyToCheck: true } as any);

  // IMPORTANT: errorPolicy: "all" so we can show friendly UI instead of throwing
  const [createLeadBroadcast, { loading: creatingBroadcast }] =
    useMutation(CREATE_REFERRAL_LEAD_BROADCAST, {
      errorPolicy: "all",
      onError: () => { }, // silence console noise
    });
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  const currentSig = useMemo(
    () =>
      stableStringify({
        sendMethod,
        businessId: slugBizId ?? null,
        kind,
        referrerName: referrerName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.replace(/[^\d]/g, ""),
        patientKey,
        careNeed: careNeed || null,
        payerType,
        urgencyChoice,
        targetDate: targetDate || null,
        notes: notes || "",
        pricingMode,
        pricingRush: !!pricingRush,
        maxInvites,
        searchCountryId: searchCountryId ?? null,
        searchStateId: searchStateId ?? null,
        searchCityId: searchCityId ?? null,
        searchZip: searchZip.trim() || null,
        searchRadiusMiles: searchRadiusMiles ?? null,
        locationMode,
        medicaidConfirmed,
        companyName: companyName || null,
        residentAge: residentAge.trim() || null,
        residentGender: residentGender || null,
        residentCurrentSituation: residentCurrentSituation || null,
        residentCurrentLocation: residentCurrentLocation.trim() || null,
        residentBudget: residentBudget || null,
        residentPrivateRoomNeeded: residentPrivateRoomNeeded || null,
        residentCaregiverPreference: residentCaregiverPreference || null,
        amenities, // track for dirty state
      }),
    [
      sendMethod,
      slugBizId,
      kind,
      referrerName,
      email,
      phone,
      patientKey,
      careNeed,
      payerType,
      urgencyChoice,
      targetDate,
      notes,
      pricingMode,
      pricingRush,
      maxInvites,
      searchCountryId,
      searchStateId,
      searchCityId,
      searchZip,
      searchRadiusMiles,
      locationMode,
      medicaidConfirmed,
      companyName,
      residentAge,
      residentGender,
      residentCurrentSituation,
      residentCurrentLocation,
      residentBudget,
      residentPrivateRoomNeeded,
      residentCaregiverPreference,
      amenities,
    ]
  );

  const [lastSubmittedSig, setLastSubmittedSig] = useState<string | null>(null);
  const dirtySinceSubmit = lastSubmittedSig === null || currentSig !== lastSubmittedSig;

  /* ------ Live summaries for headers ------ */
  const selectedAmenityLabels = useMemo(
    () => Object.entries(amenities).filter(([, v]) => v).map(([k]) => AMENITY_LABELS[k] || k),
    [amenities]
  );
  const amenitiesCount = selectedAmenityLabels.length;
  const amenitiesPreview = summarizeList(selectedAmenityLabels, 3) || "Private room, Hoyer lift, Oxygen...";

  const srcTypeName = useMemo(() => {
    const m = srcTypesList.find((s) => String(s.id) === String(referralSourceTypeId));
    return m?.name || "";
  }, [srcTypesList, referralSourceTypeId]);

  const timingLabel =
    urgencyChoice === "ASAP" ? "ASAP (<=72h)" :
      urgencyChoice === "WEEK" ? "Within 1 week" :
        urgencyChoice === "MONTH" ? "Within 30 days" :
          (urgencyChoice === "DATE" && targetDate) ? `On ${targetDate}` : "";

  const residentDetailPills: string[] = useMemo(() => {
    const pills: string[] = [];
    if (residentAge.trim()) pills.push(`Age ${residentAge.trim()}`);
    if (residentGender) pills.push(residentGender);
    if (residentCurrentSituation) pills.push(residentCurrentSituation);
    if (residentCurrentLocation.trim()) pills.push("Current location");
    if (residentBudget) pills.push("Budget");
    if (residentPrivateRoomNeeded) pills.push("Room preference");
    if (residentCaregiverPreference) pills.push("Caregiver preference");
    return pills;
  }, [
    residentAge,
    residentGender,
    residentCurrentSituation,
    residentCurrentLocation,
    residentBudget,
    residentPrivateRoomNeeded,
    residentCaregiverPreference,
  ]);

  const residentDetailsValue: CareResidentDetailsFieldValue = useMemo(
    () => ({
      residentAge,
      gender: residentGender,
      currentSituation: residentCurrentSituation,
      currentLocationText: residentCurrentLocation,
      budget: residentBudget,
      privateRoomNeeded: residentPrivateRoomNeeded,
      caregiverPreference: residentCaregiverPreference,
    }),
    [
      residentAge,
      residentBudget,
      residentCaregiverPreference,
      residentCurrentLocation,
      residentCurrentSituation,
      residentGender,
      residentPrivateRoomNeeded,
    ],
  );

  const handleResidentDetailsFieldsChange = useCallback(
    (next: Partial<CareResidentDetailsFieldValue>) => {
      if (Object.prototype.hasOwnProperty.call(next, "residentAge")) {
        setResidentAge(next.residentAge ?? "");
      }
      if (Object.prototype.hasOwnProperty.call(next, "gender")) {
        setResidentGender(next.gender ?? "");
      }
      if (Object.prototype.hasOwnProperty.call(next, "currentSituation")) {
        setResidentCurrentSituation(next.currentSituation ?? "");
      }
      if (Object.prototype.hasOwnProperty.call(next, "currentLocationText")) {
        setResidentCurrentLocation(next.currentLocationText ?? "");
      }
      if (Object.prototype.hasOwnProperty.call(next, "budget")) {
        setResidentBudget(next.budget ?? "");
      }
      if (Object.prototype.hasOwnProperty.call(next, "privateRoomNeeded")) {
        setResidentPrivateRoomNeeded(next.privateRoomNeeded ?? "");
      }
      if (Object.prototype.hasOwnProperty.call(next, "caregiverPreference")) {
        setResidentCaregiverPreference(next.caregiverPreference ?? "");
      }
    },
    [],
  );

  const optionalPills: string[] = useMemo(() => {
    const pills: string[] = [];
    if (companyName?.trim()) pills.push("Org set");
    if (timingLabel) pills.push(timingLabel);
    if (srcTypeName) pills.push(srcTypeName);
    if (careNeed) pills.push(careNeed);
    if (payerType && payerType !== "UNKNOWN") {
      pills.push(
        ({ MEDICAID: "Medicaid", MEDICARE: "Medicare", PRIVATE: "Private", VA: "VA", UNKNOWN: "Other/Unknown" } as any)[
        payerType
        ]
      );
    }
    if (pricingMode) {
      pills.push(
        pricingMode === "CONCIERGE"
        ? pricingRush
          ? "Concierge - Rush"
          : "Concierge"
        : "System handled"
      );
    }
    if ((notes || "").trim()) pills.push("Notes added");
    return pills;
  }, [
    companyName,
    timingLabel,
    srcTypeName,
    careNeed,
    payerType,
    pricingMode,
    pricingRush,
    notes,
  ]);

  const detailsPills = useMemo(
    () => [...residentDetailPills, ...optionalPills],
    [optionalPills, residentDetailPills],
  );
  const detailsCount = detailsPills.length;
  const detailsPreview =
    summarizeList(detailsPills, 4) ||
    "Age, current situation, source, move-in timing, notes...";

  const locationPreviewChips = React.useMemo(() => {
    if (sendMethod !== "BROADCAST") return [] as string[];
    if (locationMode === "ZIP") {
      const chips: string[] = [];
      const z = (searchZip || "").trim();
      if (z) chips.push(`ZIP ${z}`);
      if (Number.isFinite(Number(searchRadiusMiles)) && Number(searchRadiusMiles) > 0) {
        chips.push(`${Number(searchRadiusMiles)} mi`);
      }
      return chips;
    }
    // CITY mode
    const chips: string[] = [];
    const cityName =
      selectedState?.cities?.find((c) => Number(c.id) === Number(searchCityId))?.name || "";
    if (cityName) chips.push(cityName);
    if (selectedState?.name) chips.push(selectedState.name);
    return chips;
  }, [sendMethod, locationMode, searchZip, searchRadiusMiles, selectedState, searchCityId]);

  const preferredLocationSummary = useMemo(() => {
    if (sendMethod !== "BROADCAST") return "";
    if (locationMode === "ZIP") {
      const zip = (searchZip || "").trim();
      if (!zip) return "";
      const radius = Number(searchRadiusMiles);
      return Number.isFinite(radius) && radius > 0
        ? `ZIP ${zip} (${radius} mi radius)`
        : `ZIP ${zip}`;
    }

    const cityName =
      selectedState?.cities?.find((c) => Number(c.id) === Number(searchCityId))?.name || "";
    return [cityName, selectedState?.name].filter(Boolean).join(", ");
  }, [locationMode, searchCityId, searchRadiusMiles, searchZip, selectedState, sendMethod]);

  /* ------ Duplicate message -> open editor ------ */
  useEffect(() => {
    const msg = String(submitError || "");
    if (/active request for resident/i.test(msg)) {
      setKeyDraft(generatePatientKey());
      setKeyEditorOpen(true);
    }
  }, [submitError]);

  /* ------ Submit flow ------ */
  const proceedSubmit = useCallback(async () => {
    // reset friendly errors
    setFriendlyError(null);
    setConfirmationEmailWarning(null);
    setNoMatches(false);

    const residentKey = patientKey;                            // display
    const residentKeyCompare = normalizeResidentKey(patientKey); // compare-safe

    const urgency = computeUrgency(urgencyChoice, targetDate);
    const desiredDate = urgencyChoice === "DATE" && targetDate ? targetDate : null;
    const careFindForm = {
      residentAge: residentAge.trim() || null,
      gender: residentGender || null,
      currentLocation: residentCurrentLocation.trim() || null,
      currentSituation: residentCurrentSituation || null,
      careNeeds: careNeed ? [careNeed] : [],
      notes: notes.trim() || null,
      payerType: payerType || null,
      budget: residentBudget || null,
      timeline: timingLabel || null,
      preferredLocation: preferredLocationSummary || null,
      privateRoomNeeded: residentPrivateRoomNeeded || null,
      caregiverPreference: residentCaregiverPreference || null,
    };
    const hasCareFindFormDetails = Boolean(
      careFindForm.residentAge ||
        careFindForm.gender ||
        careFindForm.currentLocation ||
        careFindForm.currentSituation ||
        careFindForm.careNeeds.length ||
        careFindForm.notes ||
        careFindForm.payerType ||
        careFindForm.budget ||
        careFindForm.timeline ||
        careFindForm.preferredLocation ||
        careFindForm.privateRoomNeeded ||
        careFindForm.caregiverPreference,
    );

    const extra = {
      orgName: companyName.trim() || undefined,
      patientKey: residentKey,                // display
      residentKeyDisplay: residentKey,        // explicit
      residentKeyCompare,                     // for duplicate checks
      careNeed: careNeed || null,
      payerType,
      pricing: { mode: pricingMode, isRush: pricingMode === "CONCIERGE" && !!pricingRush },
      termsAccepted: true,
      submittedVia: sendMethod,
      leadSource: {
        type: attributionSourceType,
        sourceAgentId: attributionSourceType === "AGENT_SOURCED" ? attributionAgentId : null,
        sourceAgentName: attributionSourceType === "AGENT_SOURCED" ? attributionAgentName : null,
        attributionMethod:
          attributionSourceType === "AGENT_SOURCED"
            ? attributionAgentId || attributionAgentName
              ? "TRACKED_AGENT_LINK"
              : "MANUAL_AGENT_CONFIRMATION"
            : "PLATFORM_INTAKE",
      },
      referrerContact: { name: referrerName.trim(), email: email.trim(), phone: phone.trim() },
      submittingOnBehalf: submittingOnBehalf,
      medicaidConfirmed: payerType === "MEDICAID" ? !!medicaidConfirmed : undefined,
      moveIn: { choice: urgencyChoice, date: desiredDate || undefined },
      careFindForm: hasCareFindFormDetails ? careFindForm : undefined,
      location: preferredLocationSummary
        ? { submittedPreferredLocation: preferredLocationSummary }
        : undefined,
    };
    const sendConfirmationEmail = async () => {
      const to = email.trim();
      if (!to) return true;
      const subject = "We received your request";
      const intro = referrerName.trim() || "there";
      const followUpText =
        payerType === "PRIVATE"
          ? "Our concierge team will contact you."
          : "Care providers will reach out to you.";
      const text =
        `Hi ${intro},\n\n` +
        `We received your request.\n` +
        `${followUpText}\n\n` +
        `Reference: ${residentKey}\n\n` +
        `Thank you,\nPlany365`;
      const html = `
        <div style="font-family:Segoe UI, Helvetica, Arial, sans-serif; max-width:640px; margin:auto;">
          <h2 style="margin:0 0 12px 0;">We received your request</h2>
          <p style="margin:0 0 12px 0;">Hi ${intro},</p>
          <p style="margin:0 0 12px 0;">We received your request.</p>
          <p style="margin:0 0 12px 0;">${followUpText}</p>
          <p style="margin:0 0 12px 0;"><strong>Reference:</strong> ${residentKey}</p>
          <div style="font-size:12px; color:#666;">Plany365</div>
        </div>
      `;
      try {
        await sendEmail({
          variables: {
            from_email: "no-reply@plany365.com",
            to,
            subject,
            text,
            html,
          },
        });
        return true;
      } catch (err: any) {
        console.error("Confirmation email failed:", err);
        setConfirmationEmailWarning(
          "Your request was saved, but we could not send the confirmation email. Please verify the email address or try again later.",
        );
        return false;
      }
    };

    if (sendMethod === "TARGETED") {
      if (!slugBizId) return;
      const res = await submit({
        businessId: slugBizId,
        kind,
        name: referrerName.trim(),
        orgName: companyName.trim() || undefined,
        email: email.trim(),
        phone: phone.trim(),
        desiredDate,
        notes: notes || null,
        extra: JSON.stringify(extra), // targeted expects stringified JSON
        residentKey,                  // keep sending the pretty key
        urgency,
        referralSourceTypeId: referralSourceTypeId === "" ? null : Number(referralSourceTypeId),
      });
      if (res.ok || res.reused) {
        setBroadcastTrackUrl(null);
        setLastSubmittedSig(currentSig);
        await sendConfirmationEmail();
      }
      return;
    }

    // BROADCAST
    // client ZIP format guard (do not call backend with bad ZIP)
    if (locationMode === "ZIP" && (searchZip || "").trim() && !isZipValid(searchZip)) {
      setFriendlyError("Enter a valid 5-digit ZIP (optionally +4).");
      return;
    }

    const seed = {
      residentKey,     // pretty key
      payerType,
      urgency,
      kind,
      careNeed: careNeed || null,
      intakeNotes: notes || null,
      leadSourceType: attributionSourceType,
      sourceAgentId:
        attributionSourceType === "AGENT_SOURCED" ? attributionAgentId || null : null,
      intakeRoutingStatus: "ADMIN_REVIEW",
      routingMode: "ADMIN_ASSIGN",
      assignmentOrigin: "UNASSIGNED",
      extra,
      desiredDate,
    };

    try {
      const baseFilter = {
        maxInvites: maxInvites || 5,
        // Do not hard-require country in ZIP mode; many legacy rows have null countryId.
        countryId: locationMode === "CITY" ? DEFAULT_COUNTRY_ID : undefined,
        stateId: locationMode === "CITY" ? (searchStateId ?? undefined) : undefined,
        cityId: locationMode === "CITY" ? (searchCityId ?? undefined) : undefined,
        zip: locationMode === "ZIP" ? ((searchZip || "").trim() || undefined) : undefined,
        radiusMiles:
          locationMode === "ZIP" &&
            searchRadiusMiles != null &&
            Number.isFinite(Number(searchRadiusMiles))
            ? Math.max(1, Math.min(20, Number(searchRadiusMiles))) // clamp 1..20
            : undefined,
        mustBeClaimed,
        amenities: buildAmenitiesInput(amenities), // send only true flags
      };

      const runBroadcast = async (filterOverrides?: Record<string, unknown>) =>
        createLeadBroadcast({
          variables: {
            seed,
            referrerId: submittingOnBehalf ? undefined : myReferrerId ?? undefined,
            referralSourceTypeId: referralSourceTypeId === "" ? undefined : Number(referralSourceTypeId),
            filter: { ...baseFilter, ...(filterOverrides || {}) },
          },
        });

      const first = await runBroadcast();
      const firstHasErrors = !!(first.errors && first.errors.length);
      const firstMsg = first.errors?.length ? getFirstApolloMessage(first.errors[0]) : "";
      const firstGroup = first.data?.createReferralLeadBroadcast?.group;
      const rawFirstCount = first.data?.createReferralLeadBroadcast?.inviteCount;
      const firstCount = Number.isFinite(Number(rawFirstCount)) ? Number(rawFirstCount) : null;

      if (isAuthRequiredMessage(firstMsg)) {
        setNoMatches(false);
        setFriendlyError("Please log in before sending broadcasts.");
        return;
      }

      if (firstGroup?.id) {
        setBroadcastTrackUrl(`/care/referrals/group/${firstGroup.id}`);
        setBroadcastInviteCount(firstCount);
        setLastSubmittedSig(currentSig);
        await sendConfirmationEmail();
        return;
      }

      const firstLooksLikeNoMatch =
        isNoMatchesMessage(firstMsg) ||
        (!firstHasErrors && !firstGroup?.id);

      if (firstLooksLikeNoMatch && mustBeClaimed) {
        const retry = await runBroadcast({ mustBeClaimed: false });
        const retryHasErrors = !!(retry.errors && retry.errors.length);
        const retryMsg = retry.errors?.length ? getFirstApolloMessage(retry.errors[0]) : "";
        const retryGroup = retry.data?.createReferralLeadBroadcast?.group;
        const rawRetryCount = retry.data?.createReferralLeadBroadcast?.inviteCount;
        const retryCount = Number.isFinite(Number(rawRetryCount)) ? Number(rawRetryCount) : null;

        if (isAuthRequiredMessage(retryMsg)) {
          setNoMatches(false);
          setFriendlyError("Please log in before sending broadcasts.");
          return;
        }

        if (retryGroup?.id) {
          setMustBeClaimed(false);
          setBroadcastTrackUrl(`/care/referrals/group/${retryGroup.id}`);
          setBroadcastInviteCount(retryCount);
          setLastSubmittedSig(currentSig);
          await sendConfirmationEmail();
          return;
        }

        if (isNoMatchesMessage(retryMsg) || (!retryHasErrors && !retryGroup?.id)) {
          setNoMatches(true);
          return;
        }

        setFriendlyError(retryMsg || "We could not send your broadcast. Please try again.");
        return;
      }

      if (firstLooksLikeNoMatch) {
        setNoMatches(true);
        return;
      }

      setFriendlyError(firstMsg || "We could not send your broadcast. Please try again.");
    } catch (e: any) {
      const msg = getFirstApolloMessage(e);
      if (isAuthRequiredMessage(msg)) {
        setNoMatches(false);
        setFriendlyError("Please log in before sending broadcasts.");
        return;
      }
      if (isNoMatchesMessage(msg)) {
        setNoMatches(true);
      } else {
        setFriendlyError("Something went wrong sending your request. Please try again.");
      }
    }
  }, [
    amenities,
    careNeed,
    companyName,
    createLeadBroadcast,
    currentSig,
    email,
    kind,
    maxInvites,
    medicaidConfirmed,
    myReferrerId,
    mustBeClaimed,
    notes,
    payerType,
    patientKey,
    pricingMode,
    pricingRush,
    referrerName,
    residentAge,
    residentBudget,
    residentCaregiverPreference,
    residentCurrentLocation,
    residentCurrentSituation,
    residentGender,
    residentPrivateRoomNeeded,
    searchCityId,
    searchRadiusMiles,
    searchStateId,
    searchZip,
    sendMethod,
    slugBizId,
    submit,
    submittingOnBehalf,
    urgencyChoice,
    targetDate,
    timingLabel,
    preferredLocationSummary,
  ] as any); // TS-relaxed for dependency changes

  const submittingAny = submitting || creatingBroadcast;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    if (needsOnBehalfConfirm) {
      setConfirmOnBehalfOpen(true);
      return;
    }
    await proceedSubmit();
  }, [canSubmit, needsOnBehalfConfirm, proceedSubmit]);

  /* ----------------- UI ----------------- */
  return (
    <Box sx={{ maxWidth: 960, mx: "auto", px: 2, py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Submit a Resident Placement Request
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Use this form to send a resident request or referral to Plany. Share the
        placement need, availability help, or waitlist request for the person
        you are helping. Social workers, discharge planners, family members,
        case managers, community referrers, and agents can all use this form.
      </Alert>
      <Alert
        severity="success"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => navigate("/referrals")}>
            Open Referrer Page
          </Button>
        }
      >
        Want a short introduction first? Visit <b>/referrals</b>.
      </Alert>
      <Box sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate("/referrals/portal")}
        >
          Back to Referral Portal
        </Button>
      </Box>

      {(myReferrerLoading || myReferrerError) && (
        <Alert severity={myReferrerLoading ? "info" : "warning"} sx={{ mb: 2 }}>
          {myReferrerLoading
            ? "Loading your profile..."
            : "Could not load your referrer profile. We will try your account info instead."}
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String(submitError)}
        </Alert>
      )}

      {/* Friendly messaging for Broadcast submit */}
      {noMatches && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">No exact matches found.</Typography>
            <Typography variant="body2">
              Try a quick tweak and we will look again:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                onClick={() => { setMustBeClaimed(false); handleSubmit(); }}
              >
                Include unverified providers
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSearchRadiusMiles((r) => {
                    const current = Number(r || 0) || 0;
                    const doubled = Math.min(20, Math.max(1, current * 2 || 20));
                    return doubled;
                  });
                  handleSubmit();
                }}
              >
                Expand radius
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setAmenities({
                    isPrivateRoom: false, hasPrivateBathroom: false, hasHospitalBed: false,
                    hasHoyerLift: false, hasOxygen: false, accommodatesBariatric: false
                  });
                  handleSubmit();
                }}
              >
                Clear amenity filters
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              We can also reach out to nearby homes to confirm latest availability.
            </Typography>
          </Stack>
        </Alert>
      )}

      {friendlyError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {friendlyError}
          {friendlyError === "Please log in before sending broadcasts." && (
            <>
              {" "}
              <Button
                component="a"
                href={loginHref}
                size="small"
                sx={{ minWidth: "auto", p: 0, ml: 0.5, textTransform: "none", verticalAlign: "baseline" }}
              >
                Log in
              </Button>
              {" or "}
              <Button
                component="a"
                href={signupHref}
                size="small"
                sx={{ minWidth: "auto", p: 0, textTransform: "none", verticalAlign: "baseline" }}
              >
                Sign up
              </Button>
              {" to continue."}
            </>
          )}
        </Alert>
      )}
      {confirmationEmailWarning && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {confirmationEmailWarning}
        </Alert>
      )}
      {!dirtySinceSubmit && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Your request was sent to our team. We will get back to you soon.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate("/referrals/portal")}
            >
              Back to Referral Portal
            </Button>
          </Stack>
        </Alert>
      )}

      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 2.5,
          borderColor: (t) => t.palette.divider,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.00) 100%)",
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Request Details
          </Typography>

          {/* Top control row: Broadcast toggle + inline Max Providers when in BROADCAST */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              mb: 2,
              p: 1.25,
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 2,
              bgcolor: "background.default",
            }}
            alignItems="center"
          >
            <Stack
              spacing={0.75}
              sx={{ minWidth: { xs: "100%", sm: 320 }, width: { xs: "100%", sm: "auto" } }}
            >
                <TextField
                  size="small"
                  select
                  required
                  label="Payer / Funding Source"
                  value={payerType}
                  onChange={(e) => {
                    setPayerType(e.target.value as PayerType);
                    if (e.target.value !== "MEDICAID") setMedicaidConfirmed(false);
                  }}
                  error={needsPayerType}
                  helperText={needsPayerType ? "Required" : " "}
                >
                  {PAYER_OPTIONS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </TextField>
                {payerType === "MEDICAID" && (
                  <FormControl error={!medicaidConfirmed} sx={{ mt: -0.25 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={medicaidConfirmed}
                          onChange={(e) => setMedicaidConfirmed(e.target.checked)}
                        />
                      }
                      label="I confirm the payer is Medicaid."
                    />
                    {!medicaidConfirmed && (
                      <FormHelperText>Please confirm this before sending to top matches.</FormHelperText>
                    )}
                  </FormControl>
                )}
            </Stack>

            {isProviderScoped ? (
              <Stack spacing={0.5} sx={{ minWidth: { sm: 360 }, width: { xs: "100%", sm: "auto" } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: sendMethod === "TARGETED" ? 700 : 500 }}
                    color={sendMethod === "TARGETED" ? "text.primary" : "text.secondary"}
                  >
                    Current Provider
                  </Typography>
                  <Switch
                    checked={sendMethod === "BROADCAST"}
                    onChange={(_, c) => setSendMethod(c ? "BROADCAST" : "TARGETED")}
                    sx={{
                      mx: 0.5,
                      "& .MuiSwitch-track": {
                        opacity: 0.35,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        opacity: 0.5,
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: sendMethod === "BROADCAST" ? 700 : 500 }}
                    color={sendMethod === "BROADCAST" ? "text.primary" : "text.secondary"}
                  >
                    Top Matches
                  </Typography>
                  <Chip
                    size="small"
                    color={sendMethod === "BROADCAST" ? "primary" : "default"}
                    variant={sendMethod === "BROADCAST" ? "filled" : "outlined"}
                    label={
                      sendMethod === "BROADCAST"
                        ? payerType === "PRIVATE"
                          ? "Request Review"
                          : "Broadcast On"
                        : "Targeted"
                    }
                    sx={{ ml: 0.5, height: 22 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {sendMethod === "BROADCAST"
                    ? payerType === "PRIVATE"
                      ? "Your request will be reviewed first and our team will identify strong provider matches."
                      : "Your request will be sent to the best matching providers in your selected area."
                    : `Only ${bizByUrlQ.data?.businessByUrl?.name || "this provider"} will receive this request.`}
                </Typography>
              </Stack>
            ) : (
              !!payerType && payerType !== "PRIVATE" ? (
                <Button variant="text" size="small" onClick={goPickProvider}>
                  Choose a provider instead
                </Button>
              ) : null
            )}

            {/* Inline Max Providers beside the broadcast control */}
            {sendMethod === "BROADCAST" && !!payerType && payerType !== "PRIVATE" && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Send invite to at most</Typography>

                <TextField
                  size="small"
                  type="number"
                  aria-label="maximum providers to invite"
                  value={maxInvites ?? 5}
                  onChange={(e) =>
                    setMaxInvites(Math.min(Math.max(Number(e.target.value || 5), 1), 20))
                  }
                  onBlur={(e) => {
                    // Normalize on blur (keeps your default of 5 and clamps 1-20)
                    const n = Number(e.target.value);
                    const normalized =
                      e.target.value === "" || Number.isNaN(n)
                        ? 5
                        : Math.min(Math.max(n, 1), 20);
                    setMaxInvites(normalized);
                  }}
                  sx={{ width: 100 }}
                  inputProps={{ min: 1, max: 20, inputMode: "numeric" }}
                />

                <Typography variant="body2">providers</Typography>
              </Stack>
            )}
          </Stack>

          {/* REQUIRED */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
                <Typography fontWeight={700} color={requiredIssues.length ? "error.main" : undefined}>
                  Required
                </Typography>
                <Chip
                  size="small"
                  color={requiredIssues.length ? "error" : "success"}
                  variant={requiredIssues.length ? "filled" : "outlined"}
                  label={requiredIssues.length ? `${requiredIssues.length} missing` : "Complete"}
                  sx={{ height: 22 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Request Type as radios */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    What do you need for this resident?
                  </Typography>
                  <RadioGroup
                    row
                    value={kind}
                    onChange={(_, v) => setKind((v as RequestKind) || "availability")}
                    aria-label="request-type"
                    name="request-type"
                  >
                    <FormControlLabel value="availability" control={<Radio size="small" />} label="Find current availability" />
                    <FormControlLabel value="referral" control={<Radio size="small" />} label="Help place this resident" />
                    <FormControlLabel value="waitlist" control={<Radio size="small" />} label="Add this resident to a waitlist" />
                  </RadioGroup>
                </Box>

                <TextField
                  size="small"
                  label="Your name"
                  value={referrerName}
                  onChange={(e) => setReferrerName(e.target.value)}
                  required
                  error={!referrerName.trim()}
                  helperText={
                    !referrerName.trim()
                      ? "Required"
                      : "Enter the person sending this resident request."
                  }
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    size="small"
                    label="Your email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    error={!emailValid}
                    helperText={
                      !emailValid
                        ? "Enter a valid email"
                        : "We will contact this person about the resident request."
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Your phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    error={!phoneValid}
                    helperText={
                      !phoneValid
                        ? "Enter a valid phone (7+ digits)"
                        : "We will use this if we need follow-up on the resident request."
                    }
                    sx={{ flex: 1 }}
                  />
                </Stack>

                {/* Reference (editable via dialog) */}
                <TextField
                  size="small"
                  label="Reference"
                  value={patientKey}
                  disabled
                  helperText="Auto-generated (share this when following up)."
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => {
                          setKeyDraft(patientKey || generatePatientKey());
                          setKeyEditorOpen(true);
                        }}
                        sx={{ ml: 1 }}
                      >
                        Change
                      </Button>
                    ),
                  }}
                />

                {/* On-behalf hint */}
                {isLoggedInLike &&
                  !!emailLower &&
                  !!primaryAccountEmail &&
                  primaryAccountEmail !== emailLower &&
                  !submittingOnBehalf && (
                    <Typography variant="caption" color="warning.main">
                      You're signed in as {primaryAccountEmail}. Using {emailLower} will be treated as "on behalf".
                    </Typography>
                  )}

              </Stack>
          </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded={false} sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
                <Typography fontWeight={700}>Resident Details</Typography>
                <Chip
                  size="small"
                  variant={detailsCount ? "filled" : "outlined"}
                  color={detailsCount ? "primary" : "default"}
                  label={detailsCount ? `${detailsCount} added` : "Optional"}
                  sx={{ height: 22 }}
                />
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.8, ml: 0.5, flex: 1, minWidth: 160 }}
                  noWrap
                >
                  {detailsPreview}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  If you already know these answers, add them now so our team does not need
                  to ask again.
                </Typography>
                <CareResidentDetailsFields
                  value={residentDetailsValue}
                  onChange={handleResidentDetailsFieldsChange}
                  currentLocationHelperText="Optional"
                />

                <Divider />

                <Typography variant="body2" color="text.secondary">
                  Add any other helpful details if you have them.
                </Typography>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <TextField
                    size="small"
                    label="Organization (optional)"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />

                {/* Move-in timing */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Move-in timing (optional)
                  </Typography>
                  <RadioGroup
                    row
                    value={urgencyChoice}
                    onChange={(_, v) => setUrgencyChoice((v as UrgencyChoice) || "UNKNOWN")}
                  >
                    <FormControlLabel value="ASAP" control={<Radio size="small" />} label="ASAP (<=72h)" />
                    <FormControlLabel value="WEEK" control={<Radio size="small" />} label="Within 1 week" />
                    <FormControlLabel value="MONTH" control={<Radio size="small" />} label="Within 30 days" />
                    <FormControlLabel value="DATE" control={<Radio size="small" />} label="Pick a date" />
                  </RadioGroup>

                  {urgencyChoice === "DATE" && (
                    <TextField
                      size="small"
                      type="date"
                      label="Move-in date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                      sx={{ mt: 1, width: 220 }}
                      error={urgencyChoice === "DATE" && !targetDate}
                      helperText={urgencyChoice === "DATE" && !targetDate ? "Please choose a date" : " "}
                    />
                  )}
                </Box>

                <TextField
                  size="small"
                  select
                  label={
                    srcTypesLoading
                      ? "How are you connected to this resident? (loading...)"
                      : srcTypesError
                        ? "How are you connected to this resident? (defaults)"
                        : "How are you connected to this resident?"
                  }
                  value={referralSourceTypeId === "" ? "" : String(referralSourceTypeId)}
                  onChange={(e) =>
                    setReferralSourceTypeId(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  helperText="Example: family, hospital, social worker, or other referral source."
                >
                  <MenuItem value="">
                    <em>Not specified</em>
                  </MenuItem>
                  {srcTypesList.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  select
                  label="Care Need"
                  value={careNeed}
                  onChange={(e) => setCareNeed(e.target.value)}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  {CARE_NEED_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </TextField>
                {payerType === "UNKNOWN" && (
                  <Alert severity="warning">
                    Other / Unknown stays system-handled until your team reviews it.
                  </Alert>
                )}
                {pricingMode === "CONCIERGE" ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={pricingRush}
                        onChange={(e) => setPricingRush(e.target.checked)}
                      />
                    }
                    label="Concierge Rush (<=72h)"
                  />
                ) : null}
                  <TextField
                    size="small"
                    label="Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    minRows={2}
                  />
                </Box>

                {sendMethod === "BROADCAST" ? (
                  <>
                    <Divider />

                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {payerType === "PRIVATE"
                          ? "Request Review Details"
                          : "Match Preferences"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payerType === "PRIVATE"
                          ? "These details help our team review the request before sending it out."
                          : "These details help us find the best provider matches."}
                      </Typography>
                    </Box>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                            width: "100%",
                          }}
                        >
                          <Typography fontWeight={700}>Amenities</Typography>
                          <Chip
                            size="small"
                            variant={amenitiesCount ? "filled" : "outlined"}
                            color={amenitiesCount ? "primary" : "default"}
                            label={amenitiesCount ? `${amenitiesCount} selected` : "None"}
                            sx={{ height: 22 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.8, ml: 0.5, flex: 1, minWidth: 160 }}
                            noWrap
                          >
                            {amenitiesPreview}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                          Select any that apply
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 0.5 }}>
                          {([
                            ["isPrivateRoom", "Private room"],
                            ["hasPrivateBathroom", "Private bathroom"],
                            ["hasHospitalBed", "Hospital bed"],
                            ["hasHoyerLift", "Hoyer lift"],
                            ["hasOxygen", "Oxygen"],
                            ["accommodatesBariatric", "Bariatric friendly"],
                          ] as const).map(([key, label]) => (
                            <FormControlLabel
                              key={key}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={(amenities as any)[key]}
                                  onChange={(e) =>
                                    setAmenities((f) => ({ ...f, [key]: e.target.checked }))
                                  }
                                />
                              }
                              label={<Typography variant="body2">{label}</Typography>}
                              sx={{ m: 0, mr: 2 }}
                            />
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </>
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {sendMethod === "BROADCAST" ? (
            <Accordion defaultExpanded sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color={locationIssues.length ? "error.main" : undefined}
                  >
                    Location
                  </Typography>
                  {locationIssues.length ? (
                    <Chip size="small" color="error" variant="filled" label={locationIssues[0]} sx={{ height: 22 }} />
                  ) : (
                    <Chip size="small" color="success" variant="outlined" label="Complete" sx={{ height: 22 }} />
                  )}

                  {!!locationPreviewChips.length && (
                    <Stack direction="row" spacing={0.5} sx={{ ml: 0.5, flexWrap: "wrap" }}>
                      {locationPreviewChips.map((txt) => (
                        <Chip key={txt} size="small" variant="outlined" label={txt} sx={{ height: 22 }} />
                      ))}
                    </Stack>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Add the search location here. This is required so we can match the request.
                  </Typography>

                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={locationMode}
                      onChange={(_, v) => setLocationMode((v as LocationMode) || "ZIP")}
                    >
                      <FormControlLabel value="ZIP" control={<Radio size="small" />} label="By ZIP" />
                      <FormControlLabel value="CITY" control={<Radio size="small" />} label="By City" />
                    </RadioGroup>
                  </FormControl>

                  {locationMode === "ZIP" && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        size="small"
                        label="ZIP code"
                        value={searchZip}
                        onChange={(e) => setSearchZip(e.target.value)}
                        required
                        autoComplete="postal-code"
                        error={invalidZip || needsZipInZipMode}
                        helperText={
                          invalidZip
                            ? "Enter a valid 5-digit ZIP (optionally +4)."
                            : needsZipInZipMode
                              ? "Required"
                              : " "
                        }
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Radius (miles)"
                        value={searchRadiusMiles ?? ""}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          const clamped = Number.isFinite(n) ? Math.max(1, Math.min(20, n)) : null;
                          setSearchRadiusMiles(clamped);
                        }}
                        sx={{ width: 180 }}
                        inputProps={{ min: 1, max: 20, step: 1 }}
                      />
                    </Stack>
                  )}

                  {locationMode === "CITY" && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        size="small"
                        select
                        label="State / Region"
                        value={asSelectValue(searchStateId)}
                        onChange={(e) => {
                          const nextId = e.target.value ? Number(e.target.value) : null;
                          setSearchStateId(nextId);
                          setSearchCityId(null);
                        }}
                        disabled={!hasStates}
                        helperText={!hasStates ? (loadingCountries ? "Loading states..." : "Select a state source") : " "}
                        sx={{ minWidth: 220 }}
                      >
                        <MenuItem value="">
                          <em>{hasStates ? "Any state" : "-"}</em>
                        </MenuItem>
                        {selectedCountry?.states?.map((s) => (
                          <MenuItem key={String(s.id)} value={String(s.id)}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        size="small"
                        select
                        label="City"
                        value={asSelectValue(searchCityId)}
                        onChange={(e) =>
                          setSearchCityId(e.target.value ? Number(e.target.value) : null)
                        }
                        required
                        disabled={!hasCities}
                        error={needsCityInCityMode}
                        helperText={
                          !hasCities
                            ? "Select a state to choose cities"
                            : needsCityInCityMode
                              ? "Required"
                              : " "
                        }
                        sx={{ minWidth: 220 }}
                      >
                        <MenuItem value="">
                          <em>{hasCities ? "Any city" : "-"}</em>
                        </MenuItem>
                        {selectedState?.cities?.map((c) => (
                          <MenuItem key={String(c.id)} value={String(c.id)}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ) : null}

          <Divider sx={{ my: 2 }} />
          {!dirtySinceSubmit && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Your resident request was sent to our team. We will get back to you soon.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  No changes since your last request.
                </Typography>
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate("/referrals/portal")}
                  >
                    Back to Referral Portal
                  </Button>
                </Box>
              </Stack>
            </Alert>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!canSubmit || submittingAny || !dirtySinceSubmit}
          >
            {submittingAny
              ? "Submitting..."
              : sendMethod === "BROADCAST"
                ? payerType === "PRIVATE"
                  ? "Submit Resident Request"
                  : "Send Resident Request to Top Matches"
                : "Send Resident Request to Provider"}
          </Button>

          {(trackUrl || broadcastTrackUrl) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Track your request:&nbsp;
              <a href={(broadcastTrackUrl ?? trackUrl)!} target="_blank" rel="noreferrer">
                {broadcastTrackUrl ?? trackUrl}
              </a>
              <br />
              <br />
              {payerType === "PRIVATE"
                ? "Your placement specialist will review this request and send it to providers."
                : "We'll notify you once providers respond."}
              {payerType !== "PRIVATE" && sendMethod === "BROADCAST" && typeof broadcastInviteCount === "number" && (
                <> Sent to {broadcastInviteCount} provider{broadcastInviteCount === 1 ? "" : "s"}.</>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      <DuplicateDialog
        open={duplicateDialog.dialogOpen}
        onClose={duplicateDialog.handleClose}
        existing={duplicateDialog.existing}
        input={duplicateDialog.input}
        onDecision={duplicateDialog.handleDecision}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={clearToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MUIAlert onClose={clearToast} severity="success" variant="filled" elevation={6}>
          {toast}
        </MUIAlert>
      </Snackbar>

      {/* On-behalf confirmation */}
      <Dialog open={confirmOnBehalfOpen} onClose={() => setConfirmOnBehalfOpen(false)}>
        <DialogTitle>Use a different referrer?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You're signed in as <b>{primaryAccountEmail || "your account"}</b>, but entered{" "}
            <b>{email || "(no email)"}.</b>
            This will be treated as a different referrer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOnBehalfOpen(false);
              setConsent(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOnBehalfOpen(false);
              setSubmittingOnBehalf(true);
              setConsent(true);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reference editor dialog */}
      <Dialog open={keyEditorOpen} onClose={() => setKeyEditorOpen(false)}>
        <DialogTitle>Change Reference</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            A referral with this reference already exists. Choose a new one (format <code>REF-XXXX-XXXX</code>).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Reference"
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value.toUpperCase())}
            helperText={KEY_RE.test(keyDraft) ? "Looks good." : "Use letters/numbers (no O/I/0/1), like REF-ABCD-EFGH."}
            error={!KEY_RE.test(keyDraft)}
            sx={{ mt: 1 }}
          />
          <Button
            size="small"
            onClick={() => setKeyDraft(generatePatientKey())}
            sx={{ mt: 1 }}
          >
            Regenerate
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyEditorOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!KEY_RE.test(keyDraft)}
            onClick={() => {
              setPatientKey(keyDraft);
              setKeyEditorOpen(false);
            }}
          >
            Use this reference
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
